import { Link } from 'react-router-dom'
import type { PrescriptionDetail } from '../../store/prescriptionsSlice'

interface MedicationInfoProps {
  prescription: PrescriptionDetail
}

function computeDosageDisplay(prescription: PrescriptionDetail): string {
  // If dosage_calculated was explicitly applied (dosage field matches the pattern), show it
  const dc = Number(prescription.dosage_calculated)
  if (dc && prescription.dosage === `${dc} mg`) {
    return `${dc} mg`
  }

  const doseRate = prescription.dose_rate ?? prescription.medication_dose_rate
  const weight = prescription.patient_weight
  const weightUnit = prescription.patient_weight_unit || 'kg'

  if (!doseRate || !weight) return prescription.dosage || 'N/A'

  let weightKg = Number(weight)
  let conversionNote = ''
  if (weightUnit === 'lbs') {
    weightKg = Number(weight) / 2.205
    conversionNote = `${weight} lbs ÷ 2.205 = ${weightKg.toFixed(2)} kg, `
  }

  const totalDose = doseRate * weightKg

  if (conversionNote) {
    return `${conversionNote}${doseRate} mg/kg × ${weightKg.toFixed(2)} kg = ${totalDose.toFixed(2)} mg`
  }
  return `${doseRate} mg/kg × ${weight} kg = ${totalDose.toFixed(2)} mg`
}

export function MedicationInfo({ prescription }: MedicationInfoProps) {
  const dosageDisplay = computeDosageDisplay(prescription)

  return (
    <div data-testid="medication-info-section" className="border border-border rounded-[6px] bg-surface">
      <div className="px-4 py-3 border-b border-border">
        <h2 className="text-[14px] font-medium text-text-primary">Medication Info</h2>
      </div>
      <div className="p-4">
        <div className="grid grid-cols-2 gap-x-6 gap-y-3 max-sm:grid-cols-1">
          <div>
            <span className="text-[12px] text-text-muted block mb-0.5">Medication</span>
            <Link
              to={`/medications/${prescription.medication_id}`}
              className="text-[13px] text-accent hover:text-accent-dark underline"
              data-testid="medication-name-link"
            >
              {prescription.medication_name}
            </Link>
          </div>

          <div>
            <span className="text-[12px] text-text-muted block mb-0.5">Dosage (Weight-Based Calculation)</span>
            <span className="text-[13px] text-text-primary" data-testid="medication-dosage-calculation">
              {dosageDisplay}
            </span>
          </div>

          <div>
            <span className="text-[12px] text-text-muted block mb-0.5">Frequency</span>
            <span className="text-[13px] text-text-primary" data-testid="medication-frequency">
              {prescription.frequency}
            </span>
          </div>

          <div>
            <span className="text-[12px] text-text-muted block mb-0.5">Route of Administration</span>
            <span className="text-[13px] text-text-primary" data-testid="medication-route">
              {prescription.route || 'N/A'}
            </span>
          </div>

          <div>
            <span className="text-[12px] text-text-muted block mb-0.5">Duration</span>
            <span className="text-[13px] text-text-primary" data-testid="medication-duration">
              {prescription.duration || 'N/A'}
            </span>
          </div>

          <div>
            <span className="text-[12px] text-text-muted block mb-0.5">Quantity Dispensed</span>
            <span className="text-[13px] text-text-primary" data-testid="medication-quantity">
              {prescription.quantity_dispensed}
            </span>
          </div>

          <div>
            <span className="text-[12px] text-text-muted block mb-0.5">Start Date</span>
            <span className="text-[13px] text-text-primary" data-testid="medication-start-date">
              {prescription.start_date || 'N/A'}
            </span>
          </div>

          <div>
            <span className="text-[12px] text-text-muted block mb-0.5">End Date</span>
            <span className="text-[13px] text-text-primary" data-testid="medication-end-date">
              {prescription.end_date || 'N/A'}
            </span>
          </div>
        </div>
      </div>
    </div>
  )
}
